/**
 * Patient notes data and mutation orchestration.
 *
 * The hook is the sole API-owning layer for the docked panel; presentational
 * components receive typed state and callbacks.
 */

import { useCallback, useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type {
  ClinicalNoteResponse,
  CreateNoteRequest,
  NoteAssigneeResponse,
  NoteReplyRequest,
  UpdateNoteRequest,
} from "../api/clinicalTrialsApiTypes";

/** State and operations exposed to the patient notes panel. */
export interface PatientNotesController {
  notes: ClinicalNoteResponse[];
  assignees: NoteAssigneeResponse[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  reload: () => void;
  create: (request: CreateNoteRequest) => Promise<void>;
  update: (noteId: string, request: UpdateNoteRequest) => Promise<void>;
  remove: (noteId: string) => Promise<void>;
  reply: (noteId: string, request: NoteReplyRequest) => Promise<void>;
  updateReply: (
    noteId: string,
    activityId: string,
    request: NoteReplyRequest,
  ) => Promise<void>;
  removeReply: (noteId: string, activityId: string) => Promise<void>;
  command: (
    noteId: string,
    command: "resolve" | "reopen" | "read" | "unread",
  ) => Promise<void>;
}

/**
 * Load patient-wide notes and expose refetching mutation helpers.
 *
 * @param mrn - Patient medical record number
 * @param targetNoteId - Optional exact deep-linked note to fetch beyond the first page
 * @returns Patient notes controller
 */
export function usePatientNotes(
  mrn: string,
  targetNoteId?: string,
): PatientNotesController {
  const [notes, setNotes] = useState<ClinicalNoteResponse[]>([]);
  const [assignees, setAssignees] = useState<NoteAssigneeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    Promise.all([
      clinicalTrialsService.getPatientNotes(mrn, controller.signal),
      clinicalTrialsService.getNoteAssignees(controller.signal),
      targetNoteId
        ? clinicalTrialsService.getNote(targetNoteId, controller.signal)
        : Promise.resolve(null),
    ])
      .then(([notesResponse, assigneesResponse, targetNote]) => {
        const loadedNotes = notesResponse.hits;
        setNotes(
          targetNote &&
            !loadedNotes.some((note) => note.note_id === targetNote.note_id)
            ? [targetNote, ...loadedNotes]
            : loadedNotes,
        );
        setAssignees(assigneesResponse);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Failed to load patient notes",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [mrn, revision, targetNoteId]);

  /**
   * Run a note mutation, update its row immediately, and surface failures.
   *
   * @param operation - API operation returning the current note
   * @returns Nothing
   */
  const mutate = useCallback(
    async (operation: () => Promise<ClinicalNoteResponse>): Promise<void> => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = await operation();
        setNotes((current) => {
          const exists = current.some(
            (note) => note.note_id === updated.note_id,
          );
          return exists
            ? current.map((note) =>
                note.note_id === updated.note_id ? updated : note,
              )
            : [updated, ...current];
        });
      } catch (reason: unknown) {
        setError(
          reason instanceof Error ? reason.message : "Note update failed",
        );
        throw reason;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );
  const command = useCallback(
    (noteId: string, action: "resolve" | "reopen" | "read" | "unread") =>
      mutate(() => clinicalTrialsService.commandNote(noteId, action)),
    [mutate],
  );

  return {
    notes,
    assignees,
    isLoading,
    isSaving,
    error,
    reload,
    create: (request) =>
      mutate(() => clinicalTrialsService.createNote(mrn, request)),
    update: (noteId, request) =>
      mutate(() => clinicalTrialsService.updateNote(noteId, request)),
    remove: async (noteId) => {
      setIsSaving(true);
      try {
        await clinicalTrialsService.deleteNote(noteId);
        setNotes((current) =>
          current.filter((note) => note.note_id !== noteId),
        );
      } catch (reason: unknown) {
        setError(
          reason instanceof Error ? reason.message : "Failed to delete note",
        );
        throw reason;
      } finally {
        setIsSaving(false);
      }
    },
    reply: (noteId, request) =>
      mutate(() => clinicalTrialsService.createNoteReply(noteId, request)),
    updateReply: async (noteId, activityId, request) => {
      setIsSaving(true);
      try {
        await clinicalTrialsService.updateNoteReply(
          noteId,
          activityId,
          request,
        );
        reload();
      } catch (reason: unknown) {
        setError(
          reason instanceof Error ? reason.message : "Failed to edit reply",
        );
        throw reason;
      } finally {
        setIsSaving(false);
      }
    },
    removeReply: async (noteId, activityId) => {
      setIsSaving(true);
      try {
        await clinicalTrialsService.deleteNoteReply(noteId, activityId);
        reload();
      } catch (reason: unknown) {
        setError(
          reason instanceof Error ? reason.message : "Failed to delete reply",
        );
        throw reason;
      } finally {
        setIsSaving(false);
      }
    },
    command,
  };
}
